
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Server, 
  Zap,
  RefreshCw,
  Shield
} from 'lucide-react';
import { useSystemHealth } from '@/hooks/useSystemHealth';

export const SystemHealthDashboard = () => {
  const {
    healthData,
    componentStatus,
    recentChecks,
    isLoading,
    isPerformingCheck,
    performHealthCheck,
    triggerRecovery,
    getSystemStatus,
  } = useSystemHealth();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'maintenance':
        return <Clock className="w-4 h-4 text-blue-600" />;
      default:
        return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string): 'destructive' | 'secondary' | 'outline' | 'default' => {
    switch (status) {
      case 'healthy':
        return 'default';
      case 'warning':
        return 'secondary';
      case 'error':
        return 'destructive';
      case 'maintenance':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getOverallStatusDisplay = (status: string): 'healthy' | 'degraded' | 'unhealthy' => {
    switch (status) {
      case 'healthy':
        return 'healthy';
      case 'warning':
        return 'degraded';
      case 'error':
        return 'unhealthy';
      default:
        return 'degraded';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading system health data...</p>
        </div>
      </div>
    );
  }

  const overallStatusDisplay = getOverallStatusDisplay(healthData?.overall_status || 'healthy');

  return (
    <div className="space-y-6">
      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overall System Status</CardTitle>
            <div className="flex items-center space-x-2">
              {getStatusIcon(overallStatusDisplay)}
              <Button 
                variant="outline" 
                size="sm"
                onClick={performHealthCheck}
                disabled={isPerformingCheck}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`w-3 h-3 ${isPerformingCheck ? 'animate-spin' : ''}`} />
                {isPerformingCheck ? 'Checking...' : 'Check Health'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">{overallStatusDisplay}</div>
            <p className="text-xs text-muted-foreground">
              Last updated: {new Date(healthData?.last_updated || Date.now()).toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Components</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {healthData?.components_status?.healthy || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              of {(healthData?.components_status?.healthy || 0) + (healthData?.components_status?.warning || 0) + (healthData?.components_status?.error || 0)} components
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Issues Detected</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(healthData?.components_status?.warning || 0) + (healthData?.components_status?.error || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {healthData?.components_status?.error || 0} critical, {healthData?.components_status?.warning || 0} warnings
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="components" className="space-y-6">
        <TabsList>
          <TabsTrigger value="components">Component Status</TabsTrigger>
          <TabsTrigger value="recent">Recent Checks</TabsTrigger>
          <TabsTrigger value="recovery">Recovery Actions</TabsTrigger>
        </TabsList>

        <TabsContent value="components">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {componentStatus?.map((component) => (
              <Card key={component.name}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{component.name}</CardTitle>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(component.status)}
                      <Badge variant={getStatusColor(component.status)}>
                        {component.status}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {component.response_time && (
                      <div className="flex justify-between">
                        <span>Response Time:</span>
                        <span>{component.response_time}ms</span>
                      </div>
                    )}
                    {component.uptime !== undefined && (
                      <div className="flex justify-between">
                        <span>Uptime:</span>
                        <span>{component.uptime.toFixed(1)}%</span>
                      </div>
                    )}
                    {component.error_count !== undefined && (
                      <div className="flex justify-between">
                        <span>Errors:</span>
                        <span>{component.error_count}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Last Check:</span>
                      <span>{new Date(component.last_check).toLocaleString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="recent">
          <Card>
            <CardHeader>
              <CardTitle>Recent Health Checks</CardTitle>
              <CardDescription>Latest system health check results</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentChecks?.map((check, index) => (
                  <div key={index} className="flex items-center justify-between border-b pb-2 last:border-b-0">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(check.status)}
                      <div>
                        <p className="font-medium">{check.component}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(check.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Badge variant={getStatusColor(check.status)}>
                      {check.status}
                    </Badge>
                  </div>
                ))}
                {(!recentChecks || recentChecks.length === 0) && (
                  <p className="text-center text-muted-foreground py-4">No recent checks available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recovery">
          <Card>
            <CardHeader>
              <CardTitle>Recovery Actions</CardTitle>
              <CardDescription>Automated recovery options for system issues</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Quick Actions
                  </h4>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => triggerRecovery('database')}
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Restart Database Connections
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => triggerRecovery('cache')}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Clear Cache
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => triggerRecovery('api')}
                    >
                      <Server className="w-4 h-4 mr-2" />
                      Reset API Gateway
                    </Button>
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="font-medium">System Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>System Uptime:</span>
                      <span>99.8%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Last Maintenance:</span>
                      <span>2 days ago</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Recovery Actions Available:</span>
                      <span>3</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
